import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { DepotSeal } from "@/components/DepotSeal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || null;
  const goNext = (fallback) => navigate(nextPath || fallback, { replace: true });
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [hasAdmin, setHasAdmin] = useState(true); // assume yes until we know

  // If already signed in, jump straight to admin/home (or ?next=…)
  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        goNext(r.data.is_admin ? "/admin" : "/");
      })
      .catch(() => {});
    // Also fetch site bootstrap state so the register hint reflects reality.
    axios
      .get(`${API}/auth/registration-status`)
      .then((r) => setHasAdmin(Boolean(r.data.has_admin)))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (mode === "register") {
        const { data } = await axios.post(
          `${API}/auth/register`,
          { email: email.trim(), password, name: name.trim() },
          { withCredentials: true }
        );
        toast.success(`Welcome, ${data.name}!`);
        goNext(data.is_admin ? "/admin" : "/");
      } else {
        const { data } = await axios.post(
          `${API}/auth/login`,
          { email: email.trim(), password },
          { withCredentials: true }
        );
        toast.success(`Signed in as ${data.email}`);
        goNext(data.is_admin ? "/admin" : "/");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-[#111] border-white/5">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 text-amber-500">
            <DepotSeal size={72} />
          </div>
          <CardTitle className="text-2xl text-white font-serif tracking-tight">
            {mode === "login" ? "Sign in" : "Request backend access"}
          </CardTitle>
          <p className="text-[11px] uppercase tracking-[0.25em] text-amber-500/80 mt-2">
            North American Depots &amp; Towers
          </p>
        </CardHeader>

        <CardContent>
          <div className="flex gap-2 mb-6 p-1 bg-white/5 rounded-lg" data-testid="auth-mode-toggle">
            <Button
              type="button"
              onClick={() => setMode("login")}
              variant="ghost"
              className={`flex-1 ${
                mode === "login"
                  ? "bg-amber-500 hover:bg-amber-600 text-black"
                  : "text-white/80 hover:text-white hover:bg-white/5"
              }`}
              data-testid="mode-login-btn"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign in
            </Button>
            <Button
              type="button"
              onClick={() => setMode("register")}
              variant="ghost"
              className={`flex-1 ${
                mode === "register"
                  ? "bg-amber-500 hover:bg-amber-600 text-black"
                  : "text-white/80 hover:text-white hover:bg-white/5"
              }`}
              data-testid="mode-register-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Request access
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="block text-sm text-white/70 mb-1" htmlFor="name">
                  Name
                </label>
                <Input
                  id="name"
                  data-testid="auth-name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            )}

            <div>
              <label className="block text-sm text-white/70 mb-1" htmlFor="email">
                Email
              </label>
              <Input
                id="email"
                data-testid="auth-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div>
              <label className="block text-sm text-white/70 mb-1" htmlFor="password">
                Password
                {mode === "register" && (
                  <span className="text-white/40 text-xs ml-2">(min. 8 characters)</span>
                )}
              </label>
              <Input
                id="password"
                data-testid="auth-password"
                type="password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === "register" ? 8 : undefined}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              data-testid="auth-submit-btn"
            >
              {busy
                ? "Please wait…"
                : mode === "login"
                ? "Sign in"
                : "Create account"}
            </Button>

            {mode === "register" && (
              <p className="text-xs text-white/70 text-center pt-1" data-testid="register-hint">
                {hasAdmin
                  ? "Requests are held for the site administrator to review. Public visitors don't need an account to contribute — they can submit locations and photos directly from the site."
                  : "You'll be the site's first user — this account will automatically become the administrator."}
              </p>
            )}
          </form>

          <div className="pt-6 mt-6 border-t border-white/5 text-center">
            <Link
              to="/"
              className="text-sm text-white/40 hover:text-amber-500 transition-colors"
            >
              ← Back to site
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
